<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content modal-wide">
      <div class="modal-card modal-wide">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Document Types</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!loading && documentTypes.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no document types.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table 
              v-else
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    class="text--nowrap"
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new document type
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="documentType in documentTypes" 
                  :key="documentType.id">

                  <template v-if="editingId != documentType.id">
                    <td>
                      {{ documentType.document_type_name }}
                    </td>
                    <td>
                      {{ documentType.document_type_desc }}
                    </td>
                    <td class="text-center">
                      <v-checkbox  
                        v-model="documentType.is_for_condensed_proposal" 
                        disabled 
                      />
                    </td>
                    <td class="text-center">
                      <v-checkbox  
                        v-model="documentType.is_for_full_proposal" 
                        disabled 
                      />
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Document Type Name"
                        v-model="documentType.document_type_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Document Type description"
                        v-model="documentType.document_type_desc">
                      </v-text-field>
                    </td>
                    <td class="text-center">
                      <v-checkbox  
                        v-model="documentType.is_for_condensed_proposal" 
                      />
                    </td>
                    <td class="text-center">
                      <v-checkbox  
                        v-model="documentType.is_for_full_proposal" 
                      />
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <div 
                        v-if="editingId != documentType.id" 
                        class="flex flex--row flex--justify-center"
                        key="edit"
                      >
                        <button
                          @click="editingId = documentType.id" 
                          class="button is-small"
                        >
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(documentType.id)" 
                          class="button is-small"
                        >
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </div>
                      <div 
                        v-else
                        class="flex flex--row flex--justify-center"
                        key="save"
                      >
                        <button
                          @click="update(documentType)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Document Type Name"
                      v-model="documentType.document_type_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Document Type Description"
                      type="number"
                      v-model="documentType.document_type_desc">
                    </v-text-field>
                  </td>
                  <td class="text-center">
                    <v-checkbox  
                      v-model="documentType.is_for_condensed_proposal" 
                    />
                  </td>
                  <td class="text-center">
                    <v-checkbox  
                      v-model="documentType.is_for_full_proposal" 
                    />
                  </td>
                  <td>
                    <div class="buttons buttons-right">
                      <button
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                      <button
                        @click="adding = false" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-times has-text-grey"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-fade-transition>

        </div>

        <v-confirm-modal 
          v-model="confirmDelete.state"
          :title="confirmDelete.title"
          :message="confirmDelete.message"
          :type="confirmDelete.type"
          @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
          @cancel="confirmDelete.state = false"/>

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VSpinLoader from '@/components/base/VSpinLoader'
import VCheckbox from '@/components/base/VCheckbox'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { 
    VTextField, VConfirmModal, VFadeTransition, VSpinLoader, VCheckbox 
  },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Description' },
        { label: 'Condensed Proposal' },
        { label: 'Full Proposal' },
      ],
      documentType: {
        document_type_name: '',
        document_type_desc: null,
        is_for_condensed_proposal: true,
        is_for_full_proposal: true
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this document type?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("rfqDocumentTypes", [
      "documentTypes", "loading"
    ])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("rfqDocumentTypes", [
      "getDocumentTypes","addDocumentType", "deleteDocumentType", 
      "updateDocumentType", "toggleAllSelected"
    ]),
    ...mapMutations("rfqDocumentTypes", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.documentTypes.length == 0) {
        this.getDocumentTypes();
      }
    },
    add() {
      this.addDocumentType(this.documentType).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(documentType) {
      let data = {};

      Object.keys(this.documentType).forEach(field => {
        data[field] = documentType[field]
      });

      this.updateDocumentType(
        { documentTypeId: documentType.id, data: data }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteDocumentType(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this document type?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.modal-wide {
  width: 800px;
}

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}
.modal-card {
  border-radius: 6px;
}
.text-center {
  text-align: center;
}
</style>
